<script lang="ts">
  import { getProjectsByCustomer, getViewsByCustomer, getResultsByCustomer, saveUserMetadata } from "../../../services/api/data-handler.js";
  import { type ProjectsData, type View, type ResultsData, type Result, UserType } from "../../../services/api/types.js";
  import { onMount } from "svelte";
  import { createEventDispatcher } from "svelte";
  import { get } from "svelte/store";
  import type { GridOptions, GridApi } from "ag-grid-community";
  import { createGrid } from "ag-grid-community";
  import "ag-grid-enterprise";
  import { Input, Select } from "flowbite-svelte";
  import { selectedObjects } from "../../../services/stores/selected-objects-store.js";
  import { newChatSignal } from "../../../services/stores/new-chat-signal-store.js";
  import { userStore } from "../../../services/stores/user-store.js";
  import { theme } from "../../../services/stores/theme-store.js";
  import { getDocKey } from "../../../helpers/helpers.js";
  import { userType } from "../../../helpers/helpers.js";
  import { logError, logInfo, logWarning } from "../../../services/logging/log-handler.js";

  export let customer_id: string;
  export let customer_code: string;
  // Mark prop as used to satisfy linter when it's only passed from parent
  $: void customer_code;

  let gridOptions: GridOptions;
  let gridApi: GridApi;
  let views: View[] = [];/*  */
  let selectedViewId: string = "";
  let resultsData: ResultsData;
  let projectUilevel: number = 0;
  let groupExpanded: number = 0;
  let projectsData: ProjectsData;
  let suppressSelectionHandler = false;
  let isRestoringExpandedState = false;
  let previousViewId: string = "";

  // Storage key helper function
  function getStorageKey(customer_code: string, viewId: string): string {
    return `results_explorer_tree_${customer_code}_${viewId}`;
  }

  // Helper function to get the path for a group node
  // For AG Grid treeData, we need to get the full path from root to this node
  function getNodePath(node: any): string[] | null {
    if (!node) {
      return null;
    }
    
    // For document nodes, the path is in the data
    if (node.data?.path && !node.group) {
      return node.data.path;
    }
    
    if (node.group) {
      // For group nodes, we need to reconstruct the path
      // The most reliable method is to get it from a child node's data.path
      // since group nodes don't have their own data.path
      
      // Method 1: Try to get path from any child node's data (most reliable)
      // The child's data.path contains the full path from root, so we can slice it
      if (node.allLeafChildren && node.allLeafChildren.length > 0) {
        const firstChild = node.allLeafChildren[0];
        if (firstChild && firstChild.data?.path && Array.isArray(firstChild.data.path)) {
          // Use uiLevel instead of level - uiLevel is the visual level in the tree
          const uiLevel = node.uiLevel ?? node.level ?? 0;
          // The path up to this group's level (inclusive)
          // uiLevel is 0-based, so we need level + 1 elements
          if (uiLevel >= 0 && uiLevel < firstChild.data.path.length) {
            const fullPath = firstChild.data.path.slice(0, uiLevel + 1);
            return fullPath;
          }
        }
      }
      
      // Method 2: Try to get from any child (not just leaf)
      if (node.childrenAfterFilter && node.childrenAfterFilter.length > 0) {
        const firstChild = node.childrenAfterFilter[0];
        if (firstChild && firstChild.data?.path && Array.isArray(firstChild.data.path)) {
          const uiLevel = node.uiLevel ?? node.level ?? 0;
          if (uiLevel >= 0 && uiLevel < firstChild.data.path.length) {
            return firstChild.data.path.slice(0, uiLevel + 1);
          }
        }
      }
      
      // Method 3: Try to get from children (if they're group nodes with data)
      if (node.childrenAfterGroup && node.childrenAfterGroup.length > 0) {
        const firstChild = node.childrenAfterGroup[0];
        if (firstChild && firstChild.data?.path && Array.isArray(firstChild.data.path)) {
          const uiLevel = node.uiLevel ?? node.level ?? 0;
          if (uiLevel >= 0 && uiLevel < firstChild.data.path.length) {
            return firstChild.data.path.slice(0, uiLevel + 1);
          }
        }
      }
      
      // Method 4: Reconstruct from parent chain using keys (fallback)
      const path: string[] = [];
      let currentNode: any = node;
      
      // Traverse up the tree to build the complete path
      while (currentNode) {
        // Group nodes have a 'key' property that represents this level's value
        if (currentNode.key !== undefined && currentNode.key !== null) {
          path.unshift(String(currentNode.key));
        }
        currentNode = currentNode.parent;
      }
      
      // Return the full path if we collected any keys
      return path.length > 0 ? path : null;
    }
    return null;
  }

  // Get stored expanded paths for current view
  function getStoredExpandedPaths(): string[] {
    if (!customer_code || !selectedViewId) {
      return [];
    }
    try {
      const key = getStorageKey(customer_code, selectedViewId);
      const stored = sessionStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      logError(error, "ResultsTree", undefined, { message: "Error reading stored expanded paths" });
    }
    return [];
  }

  // Check if a group should be open by default based on stored state
  function isGroupOpenByDefault(params: any): boolean {
    if (!params || !params.node) {
      return false;
    }
    
    const nodeLevel = params.node?.uiLevel ?? -1;
    
    // Try to get path, but handle errors gracefully during tree construction
    let path: string[] | null = null;
    try {
      path = getNodePath(params.node);
    } catch (error) {
      // If we can't get the path yet (node not fully initialized), use default behavior
      logWarn("Could not get node path in isGroupOpenByDefault, using default", "ResultsTree", undefined, { error: error });
      return nodeLevel <= groupExpanded;
    }
    
    // Check if there's stored state for this view
    if (path && path.length > 0 && customer_code && selectedViewId) {
      const pathString = JSON.stringify(path);
      try {
        const key = getStorageKey(customer_code, selectedViewId);
        const stored = sessionStorage.getItem(key);
        if (stored) {
          // If stored state exists, use it for ALL levels (including project level)
          // This allows users to override defaults
          const storedPaths: string[] = JSON.parse(stored);
          return storedPaths.includes(pathString);
        }
      } catch (error) {
        logError(error, "ResultsTree", undefined, { message: "Error checking stored expanded paths in isGroupOpenByDefault" });
      }
    }

    // If no stored state exists, use default behavior:
    // Open everything at project level and above, closed below
    return nodeLevel <= groupExpanded;
  }

  // Save expanded state to sessionStorage
  function saveExpandedState() {
    if (isRestoringExpandedState || !gridApi || !customer_code || !selectedViewId) {
      return;
    }

    try {
      const expandedPaths: string[] = [];
      const pathDetails: Array<{ path: string[], level: number, uiLevel: number, key: string }> = [];
      
      gridApi.forEachNode((node) => {
        if (node.group && node.expanded) {
          // Save ALL expanded nodes, regardless of level
          // The user may have collapsed default-expanded nodes or expanded others
          const path = getNodePath(node);
          if (path && path.length > 0) {
            // Save the FULL path (from root to this node) as a string for reliable matching
            // This ensures each view's folders are tracked independently even if they have the same names
            const pathString = JSON.stringify(path);
            expandedPaths.push(pathString);
            pathDetails.push({
              path,
              level: node.level ?? -1,
              uiLevel: node.uiLevel ?? -1,
              key: node.key ?? 'unknown'
            });
          }
        }
      });

      const key = getStorageKey(customer_code, selectedViewId);
      sessionStorage.setItem(key, JSON.stringify(expandedPaths));

    } catch (error) {
      logError(error, "ResultsTree", undefined, { message: "Error saving expanded state" });
    }
  }

  // Restore expanded state from sessionStorage
  function restoreExpandedState(showGridCallback?: () => void) {
    if (!gridApi || !customer_code || !selectedViewId) {
      if (showGridCallback) showGridCallback();
      return;
    }

    try {
      const key = getStorageKey(customer_code, selectedViewId);
      const stored = sessionStorage.getItem(key);
      if (!stored) {
        if (showGridCallback) showGridCallback();
        return;
      }

      isRestoringExpandedState = true;
      const expandedPaths: string[] = JSON.parse(stored);

      // Wait for grid to be ready and nodes to be available
      // Use a longer timeout to ensure the grid has finished processing the data
      setTimeout(() => {
        // First, collect all nodes that need to be expanded, sorted by path length
        // (shorter paths = parent nodes, expand those first)
        const nodesToExpand: Array<{ node: any; path: string[] }> = [];
        const allNodePaths: Array<{ path: string[], level: number, key: string, matched: boolean, uiLevel: number }> = [];
        
        gridApi.forEachNode((node) => {
          if (node.group) {
            const path = getNodePath(node);
            if (path && path.length > 0) {
              const pathString = JSON.stringify(path);
              const matched = expandedPaths.includes(pathString);
              allNodePaths.push({
                path,
                level: node.level ?? -1,
                uiLevel: node.uiLevel ?? -1,
                key: node.key ?? 'unknown',
                matched
              });
              
              if (matched) {
                nodesToExpand.push({ node, path });
              } else {
                // Explicitly collapse nodes that are not in the stored state
                // This prevents AG Grid from auto-expanding them
                if (node.expanded) {
                  node.setExpanded(false);
                }
              }
            }
          }
        });

        // Sort by path length to expand parents before children
        nodesToExpand.sort((a, b) => a.path.length - b.path.length);

        // Expand nodes in order (parents first)
        // Expand all nodes synchronously, then wait for all events to settle
        nodesToExpand.forEach(({ node }) => {
          node.setExpanded(true);
        });
        
        // Wait longer to ensure all expansion events have fired and been suppressed
        // before resetting the flag. AG Grid may fire events asynchronously.
        setTimeout(() => {
          isRestoringExpandedState = false;
          // Show grid after restoration is complete
          if (showGridCallback) showGridCallback();
        }, 500);
      }, 300);
    } catch (error) {
      logError(error, "ResultsTree", undefined, { message: "Error restoring expanded state" });
      isRestoringExpandedState = false;
    }
  }

  // Update theme mode on the grid container when theme changes
  $: {
    const gridElement = document.querySelector("#resultsGrid");
    if (gridElement) {
      gridElement.setAttribute("data-ag-theme-mode", $theme === "dark" ? "dark" : "light");
    }
  }

  const dispatch = createEventDispatcher();

  // Define the stage order
  const stageOrder = {
    'Protocol': 0,
    'Screening': 1,
    'Included publications': 2,
    'Data extraction': 3,
    'Report': 4
  };

  let initializePromise: Promise<void>;

  $: if (customer_id) {
    initializePromise = initialize();
  }

  // Initialize customer and view selection
  async function initialize() {
    try {
      const store = get(userStore);
      const storedViewId =
        store.user?.metadata?.results_explorer?.tree?.view_by;
      views = await getViewsByCustomer(customer_id);
      const isValidView =
        storedViewId && views.some((v) => v.view_id === storedViewId);
      selectedViewId = isValidView ? storedViewId : views[0]?.view_id;
      previousViewId = selectedViewId;
      await loadResults();
      syncGridSelectionWithStore();
    } catch (error) {
      logError(error, "ResultsTree", undefined, { message: "Error initializing views" });
    }
  }

  async function loadResults() {
    if (!customer_id || !selectedViewId) return;

    // Store currently selected documents before loading new results
    const currentlySelectedDocs = [...$selectedObjects.documents];

    resultsData = await getResultsByCustomer(customer_id);
    projectsData = await getProjectsByCustomer(customer_id);

    // Apply view path transformation if view is selected
    const selectedView = views.find((v) => v.view_id === selectedViewId);
    if (selectedView) {
      projectUilevel = selectedView.path.findIndex(
        (field) => field.toLowerCase() === "project"
      );
      groupExpanded = projectUilevel;
      resultsData = {
        results: addPathToResults(resultsData, selectedView.path),
      };
    }

    updateTreeData();

    setTimeout(() => {
      gridApi.forEachNode((node) => {
        if (!node.group && node.data) {
          const wasSelected = currentlySelectedDocs.some(
            (doc) => doc.doc_key === (node.data.doc_key || getDocKey(node.data.doc_path || node.data.file_name))
          );
          if (wasSelected) {
            node.setSelected(true);
          }
        }
      });
    }, 200);
  }

  function updateTreeData() {
    if (gridApi && resultsData) {
      // Create a fresh callback that will use current selectedViewId when called
      // This ensures we're always checking the correct view's storage
      const currentViewId = selectedViewId;
      const currentCustomerCode = customer_code;
      const currentGroupExpanded = groupExpanded;
      
      const callback = (params: any): boolean => {
        if (!params || !params.node) {
          return false;
        }
        
        const nodeLevel = params.node?.uiLevel ?? -1;
        
        // Try to get path, but handle errors gracefully during tree construction
        let path: string[] | null = null;
        try {
          path = getNodePath(params.node);
        } catch (error) {
          // If we can't get the path yet (node not fully initialized), use default behavior
          logWarn("Could not get node path in callback, using default", "ResultsTree", undefined, { error: error });
          return nodeLevel <= currentGroupExpanded;
        }
        
        // Check if there's stored state for this view
        if (path && path.length > 0 && currentCustomerCode && currentViewId) {
          const pathString = JSON.stringify(path);
          try {
            const key = getStorageKey(currentCustomerCode, currentViewId);
            const stored = sessionStorage.getItem(key);
            if (stored) {
              // If stored state exists, use it for ALL levels (including project level)
              // This allows users to override defaults
              const storedPaths: string[] = JSON.parse(stored);
              return storedPaths.includes(pathString);
            }
          } catch (error) {
            logError(error, "ResultsTree", undefined, { message: "Error checking stored expanded paths" });
          }
        }

        // If no stored state exists, use default behavior:
        // Open everything at project level and above, closed below
        return nodeLevel <= currentGroupExpanded;
      };
      
      // Update the callback with the current view context
      gridOptions.isGroupOpenByDefault = callback;
      gridApi.setGridOption("isGroupOpenByDefault", callback);
      gridApi.setGridOption("groupDefaultExpanded", currentGroupExpanded);
      
      // Check if we have stored state - if so, hide grid viewport and show loading overlay during restoration
      const gridElement = document.querySelector("#resultsTree") as HTMLElement;
      const key = getStorageKey(currentCustomerCode, currentViewId);
      const stored = sessionStorage.getItem(key);
      const hasStoredState = !!stored;
      
      if (hasStoredState && gridElement) {
        // Hide the grid viewport content but keep overlay visible
        const viewport = gridElement.querySelector('.ag-body-viewport');
        if (viewport) {
          (viewport as HTMLElement).style.opacity = '0';
        }
        gridApi.showLoadingOverlay();
      }
      
      gridApi.setGridOption("rowData", resultsData.results);
      // Use requestAnimationFrame to ensure DOM is updated, then wait for grid processing
      requestAnimationFrame(() => {
        setTimeout(() => {
          gridApi.sizeColumnsToFit();
          gridApi.autoSizeAllColumns();
          // No need to restore expanded state here since isGroupOpenByDefault handles it
          // But we still call it as a fallback in case some nodes weren't caught
          restoreExpandedState(() => {
            // Show grid content and hide loading overlay after restoration completes
            if (hasStoredState && gridElement) {
              // Use requestAnimationFrame to ensure DOM updates happen together
              requestAnimationFrame(() => {
                const viewport = gridElement.querySelector('.ag-body-viewport');
                if (viewport) {
                  (viewport as HTMLElement).style.opacity = '1';
                }
                gridApi.hideOverlay();
              });
            }
          });
        }, 200);
      });
    }
  }

  function onQuickFilterChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    gridApi.setGridOption("quickFilterText", target.value);
  }

  onMount(() => {
    gridOptions = {
      defaultColDef: {
        sortable: true,
        wrapText: true,
        autoHeight: true,
      },
      rowSelection: {
        mode: "multiRow",
        groupSelects: "filteredDescendants",
        checkboxes: true,
        checkboxLocation: "autoGroupColumn",
        headerCheckbox: false,
      },
      onRowSelected: (event) => {
        if (suppressSelectionHandler) {
          return;
        }
        if (event.node.group) {
          // Only act when a project group becomes selected
          if (event.node.isSelected()) {
            handleProjectSelection(event.node);
          }
          return;
        }

        // For document rows, update selection store on both select and deselect
        if (event.node.data) {
          handleDocumentChecked('document', event.node);
        }
      },
      onRowGroupOpened: (event) => {
        // Save expanded state whenever a group is expanded or collapsed
        // But skip if we're currently restoring state
        if (!isRestoringExpandedState) {
          saveExpandedState();
        }
      },

      autoGroupColumnDef: {
        headerName: "Projects",
        minWidth: 340,
        width: 400,
        pinned: "left",
        lockPinned: true,
        wrapText: true,
        autoHeight: true,
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: (params: any) => {
            const nodeLevel = parseInt(
              params.node?.uiLevel?.toString() || "0",
              10
            );
            var key = "group";
            if (params.node?.group === false) {
              key = "document";
            } else if (nodeLevel === projectUilevel) {
              key = "project";
            }
            switch (key) {
              case "document":
                return createDocumentCell(params);
              case "project":
                return createProjectCell(params);
              default:
                return createGroupCell(params);
            }
          },
        },
      },
      treeData: true,
      groupDefaultExpanded: groupExpanded,
      isGroupOpenByDefault: isGroupOpenByDefault,
      getDataPath: (data) => data.path,
      getRowId: (params) => {
        // Use doc_key for document rows, generate unique ID for group rows
        if (params.data?.doc_key) {
          return params.data.doc_key;
        }
        // Fallback to doc_path for backward compatibility
        if (params.data?.doc_path) {
          return params.data.doc_path;
        }
        // For group rows, create a unique ID from the path
        return params.data?.path?.join('/') || `group-${Math.random()}`;
      },
      columnDefs: [],
      onGridReady: (params) => {
        loadResults();
      },
    };

    const gridElement = document.querySelector("#resultsTree") as HTMLElement;
    if (gridElement) {
      gridApi = createGrid(gridElement, gridOptions);
    }
  });

  function addPathToResults(
    content: ResultsData | Result[],
    pathFields: string[] | null = null
  ): Result[] {
    // Handle both array and ResultsData inputs
    const data = Array.isArray(content) ? content : content.results;

    // Define the stage order
    const stageOrder = {
      'Protocol': 0,
      'Screening': 1,
      'Included publications': 2,
      'Data extraction': 3,
      'Report': 4
    };

    // Filter and transform the results
    const transformedResults = data
      .filter((item: Result) => {
        return userType() === UserType.CUSTOMER
          ? item.status === "Active"
          : true;
      })
      .map((item: Result) => {
        // Create a new object with all existing properties
        const result = { ...item };
        // Initialize path array
        const pathArray: string[] = [];

        // If pathFields is provided, add specified fields to path
        if (pathFields) {
          pathFields.forEach((field) => {
            const value = item[field as keyof Result];
            if (value && typeof value === "string") {
              pathArray.push(value);
            }
          });
        }

        // Always add document as the last element
        // Result objects from API use doc_title, not doc_name
        pathArray.push(item.doc_title);

        // Add the path property to the result
        result.path = pathArray;
        return result;
      })
      .sort((a, b) => {
        // First sort by project name
        const projectA = a.project || '';
        const projectB = b.project || '';
        const projectCompare = projectA.localeCompare(projectB);
        
        // If projects are different, return that comparison
        if (projectCompare !== 0) {
          return projectCompare;
        }
        
        // If same project, sort by stage
        const stageA = a.stage;
        const stageB = b.stage;
        
        // Get the order for each stage, defaulting to a high number for unknown stages
        const orderA = stageOrder[stageA as keyof typeof stageOrder] ?? 999;
        const orderB = stageOrder[stageB as keyof typeof stageOrder] ?? 999;
        
        // If stages are different, return that comparison
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        // If same stage, sort by document title
        // Result objects from API use doc_title, not doc_name
        const docTitleA = a.doc_title || '';
        const docTitleB = b.doc_title || '';
        return docTitleA.localeCompare(docTitleB);
      });

    return transformedResults;
  }

  // Add store subscription to sync grid selection with store
  $: if (gridApi && $selectedObjects) {
    syncGridSelectionWithStore();
  }

  function syncGridSelectionWithStore() {
    gridApi.forEachNode((node) => {
      if (!node.group && node.data) {
        const isSelected = $selectedObjects.documents.some(
          (document) => document.doc_key === (node.data.doc_key || getDocKey(node.data.doc_path || node.data.file_name))
        );
        node.setSelected(isSelected);
      }
    });
  }

  async function onViewChange() {
    try {
      // Save expanded state for the previous view before switching
      // selectedViewId has already been updated by bind:value, so we use previousViewId
      if (previousViewId && gridApi && customer_code && previousViewId !== selectedViewId) {
        const expandedPaths: string[] = [];
        gridApi.forEachNode((node) => {
          if (node.group && node.expanded) {
            const path = getNodePath(node);
            if (path && path.length > 0) {
              // Save the FULL path to ensure view-specific state
              expandedPaths.push(JSON.stringify(path));
            }
          }
        });
        const previousKey = getStorageKey(customer_code, previousViewId);
        sessionStorage.setItem(previousKey, JSON.stringify(expandedPaths));
      }

      const store = get(userStore);
      if (!store.user) return;

      const newMetadata = {
        ...store.user.metadata,
        results_explorer: {
          ...store.user.metadata?.results_explorer,
          tree: {
            ...store.user.metadata?.results_explorer?.tree,
            view_by: selectedViewId,
          },
        },
      };
      await saveUserMetadata(newMetadata);

      // Get the selected view and update the path
      const selectedView = views.find((v) => v.view_id === selectedViewId);
      if (selectedView) {
        projectUilevel = selectedView.path.findIndex(
          (field) => field.toLowerCase() === "project"
        );
        groupExpanded = projectUilevel;
        
        // Update the path for existing results
        if (resultsData) {
          resultsData = {
            results: addPathToResults(resultsData, selectedView.path),
          };
          updateTreeData();
          // Selections should be preserved automatically by AG Grid via getRowId
          // Expanded state will be restored by updateTreeData -> restoreExpandedState
        }
      }

      // Update previous view ID for next change (after we've saved the old one)
      previousViewId = selectedViewId;
    } catch (error) {
      logError(error, "ResultsTree", undefined, { message: "Error updating view preference" });
    }
  }

  function createDocumentCell(params: any) {
    const span = document.createElement("span");
    span.classList.add("grid-cell");
    span.appendChild(createATag(params, params.value, "document"));
    return span;
  }

  function createProjectCell(params: any) {
    const span = document.createElement("span");
    span.classList.add("grid-cell");
    
    // Create container for project name and count
    const container = document.createElement("div");
    container.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    // Find project in projectsData
    const project = projectsData?.projects?.find(p => p.name === params.value);
    const docCount = project?.current_upload?.document_count || 0;
    
    // Check if project is new (less than 7 days old)
    const isNew = project?.current_upload?.date && 
      (new Date().getTime() - new Date(project.current_upload.date).getTime()) < (7 * 24 * 60 * 60 * 1000);

    // Create project name with count
    const nameSpan = document.createElement("span");
    nameSpan.textContent = `${params.value}`;
    nameSpan.classList.add("project-name");
    container.appendChild(nameSpan);

    // Create the count span
    const countIndicator = document.createElement("span");
    countIndicator.textContent = `(${docCount})`;
    countIndicator.classList.add("count");
    container.appendChild(countIndicator); 

    // Create NEW indicator if applicable
    if (isNew) {
      const newIndicator = document.createElement("span");
      newIndicator.textContent = "NEW";
      newIndicator.classList.add("new");
      newIndicator.title = "Updated in the last 7 days";
      container.appendChild(newIndicator);
    }

    // Create the link with the container's content
    const link = createATag(params, params.value, "project");
    link.textContent = ''; // Clear the default text
    link.appendChild(container);
    span.appendChild(link);

    // Add tooltip to the outer span
    if (project?.current_upload?.date) {
      const uploadDate = new Date(project.current_upload.date);
      const formattedDate = uploadDate.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }).toUpperCase();
      span.title = `Documents added on ${formattedDate}`;
    }

    return span;
  }

  function createGroupCell(params: any) {
    const span = document.createElement("span");
    span.classList.add("grid-cell");
    span.appendChild(createSpan(params.value));
    return span;
  }

  function handleProjectSelection(node: any) {
    if (!node.data) {
      return;
    }

    gridApi.deselectAll();

    if (node.allLeafChildren) {
      node.allLeafChildren.forEach((child: import('ag-grid-community').RowNode) => {
        child.setSelected(true);
        handleDocumentChecked("project", child);
      });
    }
  }

  function createATag(params: any, value: string, key: string) {
    const a = document.createElement("a");
    a.href = "javascript:void(0)";
    a.classList.add("document-link");
    a.style.cssText = `
      white-space: normal;
      word-break: break-word;
      line-height: 1.2em;       
      display: block;
      margin: 0;
      padding: 4px 0;
      text-decoration: none;
      color: inherit;
      cursor: pointer;
    `;
    a.textContent = value;
    a.addEventListener("click", async (e) => {
      e.preventDefault();

      // Hyperlink clicks should start a new chat and set context to only this item
      // First, clear all selections
      selectedObjects.clearDocuments();
      gridApi.deselectAll();

      if (key === "project") {
        // For projects, select all documents in this project
        suppressSelectionHandler = true;
        try {
          if (params.node.allLeafChildren) {
            params.node.allLeafChildren.forEach((child: import('ag-grid-community').RowNode) => {
              child.setSelected(true);
              handleDocumentChecked("project", child);
            });
          }
          // Trigger new chat signal after selection is complete
          newChatSignal.trigger();
        } finally {
          suppressSelectionHandler = false;
        }
      } else {
        // For documents, select only this one
        suppressSelectionHandler = true;
        try {
          params.node.setSelected(true);
          handleDocumentChecked('document', params.node);
          // Trigger new chat signal after selection is complete
          newChatSignal.trigger();
        } finally {
          suppressSelectionHandler = false;
        }
      }
    });
    return a;
  }

  function createSpan(value: string) {
    const span = document.createElement("span");
    span.style.cssText = `
      white-space: normal;
      word-break: break-word;
      line-height: 1.2em;       
      display: block;
      margin: 0;
      padding: 2px 0;
    `;
    span.textContent = value;

    return span;
  }

  function handleDocumentChecked(key: 'document' | 'project', params: any) {
    const isSelected = params.isSelected();
    const docPath = params.data.doc_path || params.data.file_name; // Storage file name (blob storage)
    const docKey = params.data.doc_key || getDocKey(docPath); // Extract doc_key if not provided
    const projectId = params.data.project_id;

    if (!isSelected) {
      selectedObjects.remove(docKey);
      return;
    }

    // When explicitly selecting a single document, ensure the project is not marked fully-selected
    if (key === 'document' && $selectedObjects.projects.some(p => p.project_id === projectId)) {
      selectedObjects.removeProject(projectId);
    }

    // Look up total documents for this project from projectsData
    const project = projectsData?.projects?.find(p => p.project_id === projectId);
    const project_total_docs = project?.current_upload?.document_count;

    // Map API fields to store fields:
    // doc_path (API) -> file_name (store) - blob storage name
    // file_name or doc_title (API) -> doc_name (store) - user-friendly name
    const docName = params.data.file_name || params.data.doc_title;

    selectedObjects.add({
      file_name: docPath, // Blob storage name (was doc_path)
      doc_name: docName, // User-friendly name (was doc_title)
      doc_key: docKey, // Unique identifier (was key)
      customer_code: params.data.customer_code,
      project_id: projectId,
      project_name: params.data.project,
      document_id: params.data.document_id,
      stage: params.data.stage, // Document stage (Protocol, Screening, etc.)
      project_total_docs
    });
  }
  
</script>

<svelte:head>
</svelte:head>

<div class="flex items-center mt-1 mb-1">
  <span class="app-tree-label">View by:</span>
  <Select
    class="app-field w-40"
    bind:value={selectedViewId}
    on:change={() => onViewChange()}
  >
    {#each views as view}
      <option value={view.view_id}>{view.name}</option>
    {/each}
  </Select>
  <span class="app-tree-label">Filter:</span>
  <Input type="text" on:input={onQuickFilterChanged} class="app-field w-20" />
</div>

<div id="resultsTree" class="ag-theme-quartz ag-tree" data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"></div>
